import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Paperclip, X, Loader2, Image as ImageIcon } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB raw
const ACCEPTED = ["image/png", "image/jpeg", "image/webp", "image/heic", "image/heif"];

/**
 * File picker + preview. Emits a base64 data URL via `onChange(dataUrl|"")`.
 * Renders a compact chip when an attachment is staged, with a remove button.
 */
export function InboxAttachmentPicker({ value, onChange, disabled, onError }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = (file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      onError?.("Attachment must be PNG, JPEG, or WEBP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      return;
    }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      onChange?.(String(reader.result || ""));
      setBusy(false);
    };
    reader.onerror = () => {
      onError?.("Could not read file. Please try another.");
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const clear = () => {
    onChange?.("");
    if (inputRef.current) inputRef.current.value = "";
  };

  if (value) {
    return (
      <div className="flex items-center gap-2" data-testid="inbox-attachment-preview">
        <img src={value} alt="attachment preview"
          className="h-12 w-12 object-cover rounded border border-[#FBBF24]/40" />
        <span className="text-[11px] text-white/60 flex items-center gap-1">
          <ImageIcon size={12} /> Image attached
        </span>
        <button type="button" onClick={clear} disabled={disabled}
          className="text-white/50 hover:text-[#DC2626] p-1"
          data-testid="inbox-attachment-remove" aria-label="Remove attachment">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(",")}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="inbox-attachment-input"
      />
      <button
        type="button"
        onClick={pick}
        disabled={disabled || busy}
        className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#FBBF24] px-2 py-1.5 border border-white/10 hover:border-[#FBBF24]/40 rounded transition-colors disabled:opacity-40"
        data-testid="inbox-attachment-button"
        title="Attach an image (PNG/JPEG/WEBP, max 5 MB)"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Attach image
      </button>
    </>
  );
}

/**
 * Renders a private (authed) inbox image via fetch → blob URL, then opens
 * a lightbox on click for zooming.
 */
export function InboxAttachmentImage({ url }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!url) return;
    let created = "";
    let cancelled = false;
    (async () => {
      try {
        // `url` looks like `/api/inbox/{tid}/messages/{mid}/attachment`.
        // Strip the leading `/api` prefix so axios (baseURL already ends in /api) doesn't double it.
        const path = url.replace(/^\/api/, "");
        const res = await api.get(path, { responseType: "blob" });
        if (cancelled) return;
        created = URL.createObjectURL(res.data);
        setBlobUrl(created);
      } catch (_e) {
        setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  if (failed) {
    return <div className="text-[11px] text-[#FCA5A5] italic mt-1">Attachment failed to load.</div>;
  }
  if (!blobUrl) {
    return (
      <div className="mt-2 h-24 w-40 rounded border border-white/10 bg-white/5 flex items-center justify-center">
        <Loader2 size={14} className="animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="mt-2 block rounded border border-white/10 overflow-hidden hover:border-[#FBBF24]/50 transition-colors"
        data-testid="inbox-message-attachment"
      >
        <img src={blobUrl} alt="attachment" className="max-h-64 max-w-full object-contain bg-black/40" />
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          data-testid="inbox-attachment-lightbox"
        >
          <img src={blobUrl} alt="attachment full" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </>
  );
}
